import React from 'react';
import { Pagination, Text, Flex, ButtonGroup, IconButton, HStack } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Select from './Select';

export interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface PaginationProps {
  paginationInfo: PaginationInfo;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showPageSizeSelector?: boolean;
  isLoading?: boolean;
}

const PaginationComponent: React.FC<PaginationProps> = ({
  paginationInfo,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200, 500],
  showPageSizeSelector = true,
  isLoading = false,
}) => {
  const { current_page, last_page, per_page, total } = paginationInfo;

  // Don't show pagination if there's only one page or no data and no page size selector
  if (last_page <= 1 && total === 0) {
    return null;
  }

  const startItem = (current_page - 1) * per_page + 1;
  const endItem = Math.min(current_page * per_page, total);

  // Generate page size options for select
  const pageSizeSelectOptions = pageSizeOptions.map(size => ({
    value: size.toString(),
    label: `${size} per page`
  }));

  const handlePageSizeChange = (value: string) => {
    const newPageSize = parseInt(value, 10);
    onPageSizeChange?.(newPageSize);
  };

  return (
    <Flex
      justify="space-between"
      align="center"
      py={4}
      px={2}
      borderTop="1px"
      borderColor="gray.200"
      _dark={{ borderColor: "gray.600" }}
    >
      {/* Results info */}
      <Text
        fontSize="sm"
        color="gray.600"
        _dark={{ color: "gray.400" }}
      >
        Showing {startItem} to {endItem} of {total} results
      </Text>

      {/* Pagination and page size controls */}
      <HStack gap={4}>
        {/* Page size selector */}
        {showPageSizeSelector && (
          <Select
            options={pageSizeSelectOptions}
            value={per_page.toString()}
            onChange={handlePageSizeChange}
            placeholder="Page size"
            size="sm"
            width="120px"
            disabled={isLoading}
          />
        )}

        {/* Pagination controls */}
        {last_page > 1 && (
          <Pagination.Root
            count={total}
            pageSize={per_page}
            page={current_page}
            onPageChange={(details) => onPageChange(details.page)}
          >
            <ButtonGroup variant="ghost" size="sm">
              <Pagination.PrevTrigger asChild>
                <IconButton disabled={isLoading}>
                  <ChevronLeft size={16} />
                </IconButton>
              </Pagination.PrevTrigger>

              <Pagination.Items
                render={(page) => (
                  <IconButton 
                    variant={{ base: "ghost", _selected: "outline" }}
                    disabled={isLoading}
                  >
                    {page.value}
                  </IconButton>
                )}
              />

              <Pagination.NextTrigger asChild>
                <IconButton disabled={isLoading}>
                  <ChevronRight size={16} />
                </IconButton>
              </Pagination.NextTrigger>
            </ButtonGroup>
          </Pagination.Root>
        )}
      </HStack>
    </Flex>
  );
};

export default PaginationComponent; 